import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExpandMoreIcon from '@mui/icons-material/KeyboardArrowUp';
import { Accordion, AccordionDetails, AccordionSummary, Box, styled } from '@mui/material';

export type AccordionProps = {
  title: string;
  titelSize?: string;
  titleSlot?: React.ReactNode;
  infoFontSize?: string;
  infoContent?: string;
  infoIcon?: IconDefinition;
  infoIconColor?: string;
  content?: React.ReactNode;
  defaultExpanded?: boolean;
};

const StyledTitle = styled(TypographyTsoPq, {
  shouldForwardProp: (prop) => !['titelSize'].includes(prop as string),
})(({ titelSize }: { titelSize?: string }) => {
  return {
    fontSize: titelSize,
    lineHeight: 1.2,
  };
});

const StyledInfo = styled(TypographyTsoPq, {
  shouldForwardProp: (prop) => !['infoFontSize'].includes(prop as string),
})(({ infoFontSize }: { infoFontSize?: string }) => {
  return {
    fontSize: infoFontSize,
    lineHeight: 1.2,
  };
});

const StyledAccordion = styled(Accordion)({
  borderRadius: tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_4,
  border: `1px solid ${tsoPqTheme.palette.stroke.color_neutral_stroke_str_ntrl_disabled}`,
  boxShadow: 'none',
  width: '100%',

  '&.Mui-expanded': {
    margin: `0 0 0${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_8}`,
  },
});

const StyledInfoWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
  alignSelf: 'stretch',
  padding: `0 0 0${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_16}`,
});

interface StyledExpandIconProps {
  expanded?: boolean;
}

const StyledExpandIcon = styled(ExpandMoreIcon, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<StyledExpandIconProps>(({ expanded }) => ({
  color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
  transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: 'transform 0.2s',
}));

const SytledAccordionSummary = styled(AccordionSummary)({
  flexDirection: 'row-reverse',
  minHeight: 0,
  gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
  padding: `${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_16} 0 ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_16} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_16}`,
  '&.Mui-expanded': { minHeight: 0 },
  '& .MuiAccordionSummary-content': {
    padding: 0,
    margin: 0,
    alignItems: 'center',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_16,
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    padding: 0,
    margin: 0,
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_16,
  },
});

const SytledAccordionDetails = styled(AccordionDetails)({
  padding: `0 ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_16} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_16} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_16}`,
});

const StyledContentWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  alignSelf: 'stretch',
  borderRadius: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4,
  background: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_neutral_lt,
  padding: tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_24,
  width: '100%',
  boxSizing: 'border-box',
});

const StyledIconBox = styled(Box, {
  shouldForwardProp: (prop) => !['type', 'infoContent', 'infoFontSize'].includes(prop as string),
})(() => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
  };
});

const AccordionTsoPq = ({
  title,
  titleSlot,
  titelSize,
  content,
  infoContent,
  infoFontSize,
  infoIcon,
  infoIconColor,
  defaultExpanded,
}: AccordionProps) => {
  const fallbackColor = tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled;

  return (
    <StyledAccordion defaultExpanded={defaultExpanded}>
      <SytledAccordionSummary expandIcon={<StyledExpandIcon />}>
        {title && (
          <StyledTitle typographyType={'pageSubtitleMDBold'} titelSize={titelSize}>
            {title}
          </StyledTitle>
        )}
        {titleSlot && <Box>{titleSlot}</Box>}
      </SytledAccordionSummary>
      <SytledAccordionDetails>
        {infoContent && (
          <StyledInfoWrapper>
            <StyledIconBox>
              {infoIcon && (
                <FontAwesomeIcon
                  icon={infoIcon}
                  style={{ color: infoIconColor ?? fallbackColor, width: '1rem', height: '1rem' }}
                />
              )}
              {
                <StyledInfo infoFontSize={infoFontSize} typographyType={'pageSubtitleMD'}>
                  {infoContent}
                </StyledInfo>
              }
            </StyledIconBox>
          </StyledInfoWrapper>
        )}
        {content && <StyledContentWrapper>{content}</StyledContentWrapper>}
      </SytledAccordionDetails>
    </StyledAccordion>
  );
};

export default AccordionTsoPq;
